const INSTAGRAM_DOMAIN = /instagram\.com/i;
const INSTAGRAM_PROFILE_URL = /^https?:\/\/(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)\/?(?:\?.*)?$/i;

const YOUTUBE_DOMAIN = /youtube\.com|youtu\.be/i;
// Channel URLs are often copied straight off a tab (e.g. .../@handle/videos,
// /shorts, /posts) rather than the bare profile page — that trailing tab
// segment is still the same channel, so it's optional here rather than
// treated as a different (rejected) path shape.
const YOUTUBE_PROFILE_URL = /^https?:\/\/(?:www\.)?(?:youtube\.com\/(?:channel\/|c\/|@)|youtu\.be\/)([a-zA-Z0-9._-]+)(?:\/(?:videos|shorts|streams|playlists|community|channels|about|posts))?\/?(?:\?.*)?$/i;

const TWITTER_DOMAIN = /(?:^|\/\/|\.)(?:x|twitter)\.com/i;
// A profile link is exactly one path segment (optionally a profile tab). Post links
// (/handle/status/123), /i/..., /home etc. are not profiles — rejected below.
const TWITTER_PROFILE_URL = /^https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/([A-Za-z0-9_]{1,15})(?:\/(?:with_replies|media|highlights|articles))?\/?(?:\?.*)?$/i;
const TWITTER_NON_PROFILE = new Set([
  "home", "explore", "i", "search", "notifications", "messages", "settings", "compose",
  "hashtag", "intent", "share", "login", "signup", "tos", "privacy", "about", "jobs",
  "status", "lists", "communities", "topics", "bookmarks", "premium", "verified",
]);

const LINKEDIN_DOMAIN = /linkedin\.com/i;
// Person (/in/) or company (/company/) page — anything else on linkedin.com (posts,
// feed, jobs, schools) isn't a profile. Allows a country subdomain (in.linkedin.com)
// and trailing sub-pages (/details/experience).
const LINKEDIN_PROFILE_URL = /^https?:\/\/(?:[a-z]{2,3}\.|www\.)?linkedin\.com\/(in|company)\/([^/?#]+)(?:\/[^?#]*)?(?:[?#].*)?$/i;

// Per-platform rules. `fromUrl(match)` returns the value to send to the backend for a
// URL that matched `profile` (or null to reject it); a pasted bare handle goes through
// `fromBare`. Both return the string the backend's resolveProfile() accepts.
const PLATFORM_RULES = {
  instagram: {
    domain: INSTAGRAM_DOMAIN,
    profile: INSTAGRAM_PROFILE_URL,
    fromUrl: (m) => m[1],
    fromBare: (entry) => entry.replace(/^@/, ""),
  },
  youtube: {
    domain: YOUTUBE_DOMAIN,
    profile: YOUTUBE_PROFILE_URL,
    fromUrl: (m) => m[1],
    fromBare: (entry) => entry.replace(/^@/, ""),
  },
  twitter: {
    domain: TWITTER_DOMAIN,
    profile: TWITTER_PROFILE_URL,
    fromUrl: (m) => (TWITTER_NON_PROFILE.has(m[1].toLowerCase()) ? null : m[1]),
    fromBare: (entry) => entry.replace(/^@/, ""),
  },
  linkedin: {
    domain: LINKEDIN_DOMAIN,
    profile: LINKEDIN_PROFILE_URL,
    // A company page keeps its "company/" prefix so the backend can tell it apart from a
    // person with the same slug; a person is sent as the bare slug.
    fromUrl: (m) => (m[1].toLowerCase() === "company" ? `company/${m[2]}` : m[2]),
    fromBare: (entry) => entry.replace(/^@/, "").replace(/^in\//i, ""),
  },
};

export const parseProfilesInput = (text, platform = "instagram") => {
  if (!text?.trim()) return [];

  const rules = PLATFORM_RULES[platform] || PLATFORM_RULES.instagram;

  return text
    .split(/[,\n]/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .map((entry) => {
      if (rules.domain.test(entry)) {
        // Looks like a URL for this platform — only accept it if it's shaped like a
        // plain profile link (exactly one path segment). Anything else (post/reel/
        // explore/suggestion links, e.g. instagram.com/popular/some-slug) isn't a
        // profile, and naively grabbing the first path segment would silently send
        // a garbage "username" to the paid lookup API instead of being rejected here.
        const match = entry.match(rules.profile);
        return match ? rules.fromUrl(match) : null;
      }
      return rules.fromBare(entry);
    })
    .filter(Boolean)
    // de-dupe, case-insensitive
    .filter(
      (handle, index, all) =>
        all.findIndex((h) => h.toLowerCase() === handle.toLowerCase()) === index
    );
};
